import React, { useState } from 'react';
import useMountEffect from '@restart/hooks/useMountEffect';
import classNames from 'classnames/bind';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, DatePicker, Divider, Form, Input, InputNumber, Select, Space, Typography, message } from 'antd';

import API from 'helpers/API';
import { getAddress } from 'utils/AddressFinder';
import { BottomButtons, HeadingH1 } from 'components/ui';
import { getDisplayName } from 'utils/getDisplayName';
import { formatSeoulDateTime } from 'utils/Formatter';
import { isReclaimDateDisabled } from './reclaimDateRules';
import { formatReclaimDateValue, parseReclaimDateValue } from './reclaimDateFields';

import styles from './detail.module.scss';

const cx = classNames.bind(styles);

const Page = () => {
  const { rid } = useParams();
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [reclaim, setReclaim] = useState();
  const [locations, setLocations] = useState();
  const [customerNames, setCustomerNames] = useState();

  const findAddress = () => {
    getAddress().then((data) => form.setFieldValue('customerAddress', data.address));
  };

  const doSave = (values) => {
    const data = {
      ...values,
      reclaimDate: formatReclaimDateValue(values.reclaimDate),
      quantity: Number(values.quantity),
    };

    // API: 회수 수정
    API.patch(`/reclaims/${rid}`, data).then(({ success }) => {
      if (success) {
        message.success('수정 완료하였습니다.');
        navigate('/reclaims');
      } else {
        message.error('저장을 실패했습니다.');
      }
    });
  };

  useMountEffect(() => {
    // API: 거점 내역 조회
    API.get('/locations').then(({ success, data }) => {
      if (success) {
        setLocations(data.locations);
      }
    });

    // API: 주문 내역 조회
    API.get('/orders').then(({ success, data }) => {
      if (success) {
        // 모든 고객명 배열화
        const allCustomerNames = data.orders.map((order) => order.customerName.trim());
        // 고객명 중복제거 및 오름차순 정렬
        const sortedCustomerNames = [...new Set(allCustomerNames)].sort();
        // 고객명 정의
        setCustomerNames(sortedCustomerNames);
      }
    });

    // API: 회수 상세 조회
    API.get(`/reclaims/${rid}`).then(({ success, data }) => {
      if (success) {
        form.setFieldsValue({
          ...data,
          reclaimDate: parseReclaimDateValue(data.reclaimDate),
          reclaimLocationRid: data.reclaimLocation.rid,
        });
        setReclaim({
          ...data,
          reclaimLocationRid: data.reclaimLocation.rid,
        });
      }
    });
  });

  return (
    <div className={cx('container')}>
      <Space direction="vertical" size={16} className={cx('wrapper')}>
        <HeadingH1 title={'회수 상세'} />
        <Form form={form} layout="vertical" onFinish={doSave} className={cx('form')}>
          <Typography.Text strong className={cx('title')}>
            고객 정보
          </Typography.Text>
          <div className={cx('sectionPanel')}>
            <div className={cx('infoGridThree')}>
              <Form.Item
                className={cx('formItemCompact')}
                name="customerCompany"
                label="회사명"
                rules={[{ required: true, message: '회사명을 입력해주세요' }]}>
                <Input className={cx('fullWidthControl')} />
              </Form.Item>
              <Form.Item
                className={cx('formItemCompact')}
                name="customerName"
                label="고객명"
                rules={[{ required: true, message: '고객명을 선택해주세요' }]}>
                <Select
                  className={cx('fullWidthControl')}
                  placeholder="고객명"
                  options={customerNames?.map((data) => ({ label: data, value: data })) || []}
                />
              </Form.Item>
              <Form.Item
                className={cx('formItemCompact')}
                name="customerPhone"
                label="전화번호"
                rules={[{ required: true, message: '전화번호를 입력해주세요' }]}>
                <Input className={cx('fullWidthControl')} />
              </Form.Item>
            </div>
            <div className={cx('infoGridTwoWide')}>
              <Form.Item
                className={cx('formItemCompact')}
                name="customerAddress"
                label="주소"
                rules={[{ required: true, message: '주소를 입력해주세요' }]}>
                <Input.Search onSearch={findAddress} onClick={findAddress} className={cx('fullWidthControl')} />
              </Form.Item>
              <Form.Item className={cx('formItemCompact')} name="customerAddressDetail" label="상세 주소">
                <Input className={cx('fullWidthControl')} />
              </Form.Item>
            </div>
          </div>

          <Divider />

          <Typography.Text strong className={cx('title')}>
            회수 설정
          </Typography.Text>
          <div className={cx('sectionPanel')}>
            <div className={cx('infoGridThree')}>
              <Form.Item
                className={cx('formItemCompact')}
                name="reclaimDate"
                label="회수 요청일"
                rules={[{ required: true, message: '회수 요청일을 선택해주세요' }]}>
                <DatePicker
                  placeholder="회수 요청일 선택"
                  className={cx('fullWidthControl')}
                  allowClear={false}
                  disabledDate={isReclaimDateDisabled}
                />
              </Form.Item>
              <Form.Item
                className={cx('formItemCompact')}
                name={'quantity'}
                label="수량"
                rules={[{ required: true, message: '수량을 입력해주세요' }]}>
                <InputNumber className={cx('fullWidthControl')} />
              </Form.Item>
              <Form.Item
                className={cx('formItemCompact')}
                name="reclaimLocationRid"
                label="최종 거점"
                rules={[{ required: true, message: '최종 거점을 선택해주세요' }]}>
                <Select
                  className={cx('fullWidthControl')}
                  placeholder="최종 거점"
                  options={locations?.map((data) => ({ label: data.name, value: data.rid })) || []}
                />
              </Form.Item>
            </div>
          </div>

          <Divider />

          <Typography.Text strong className={cx('title')}>
            부가 정보
          </Typography.Text>
          <div className={cx('additionalInfoGrid')}>
            <div className={cx('detailCard')}>
              <div className={cx('detailCardHeader')}>
                <Typography.Text strong>메모</Typography.Text>
              </div>
              <Form.Item name={'memo'} className={cx('memoField')}>
                <Input.TextArea className={cx('memoTextarea')} />
              </Form.Item>
            </div>
            <div className={cx('detailCard')}>
              <div className={cx('detailCardHeader')}>
                <Typography.Text strong>이력 요약</Typography.Text>
                <Typography.Text type="secondary">서울 시간 기준</Typography.Text>
              </div>
              <div className={cx('metaGrid')}>
                <div className={cx('metaCard')}>
                  <span className={cx('metaLabel')}>등록자</span>
                  <span className={cx('metaValue')}>{getDisplayName(reclaim?.createdBy)}</span>
                </div>
                <div className={cx('metaCard')}>
                  <span className={cx('metaLabel')}>등록일시</span>
                  <span className={cx('metaValue', 'metaValueCompact')}>{formatSeoulDateTime(reclaim?.createdAt)}</span>
                </div>
                <div className={cx('metaCard')}>
                  <span className={cx('metaLabel')}>수정자</span>
                  <span className={cx('metaValue')}>{getDisplayName(reclaim?.updatedBy)}</span>
                </div>
                <div className={cx('metaCard')}>
                  <span className={cx('metaLabel')}>수정일시</span>
                  <span className={cx('metaValue', 'metaValueCompact')}>{formatSeoulDateTime(reclaim?.updatedAt)}</span>
                </div>
              </div>
            </div>
          </div>

          <BottomButtons
            left={[
              <Button key={'movePrevPage'} onClick={() => navigate(-1)}>
                뒤로
              </Button>,
            ]}
            right={[
              <Button
                key={'controlSerial'}
                type="primary"
                onClick={() => navigate(`/reclaims/${rid}/tanks`)}
                disabled={reclaim?.status === ''}>
                용기 관리
              </Button>,
              <Button
                key={'controlDelivery'}
                type="primary"
                disabled={reclaim?.reclaimTanks?.length === 0}
                onClick={() => navigate(`/reclaims/${rid}/shipping`)}>
                배송 관리
              </Button>,
              <Button key={'saveForm'} type="primary" htmlType="submit">
                저장
              </Button>,
            ]}
          />
        </Form>
      </Space>
    </div>
  );
};

export default Page;
